/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * The HTTP layer.
 *
 * Attaches the Cognito access token, sends the client's clock as a DIAGNOSTIC
 * (see below), and turns non-2xx responses into a typed error carrying the
 * server's own message — which is written to be shown to a person.
 */

let getToken: (() => Promise<string | null>) | null = null;

/** Installed once by AuthBridge, so this module doesn't depend on React. */
export function setTokenProvider(fn: () => Promise<string | null>): void {
  getToken = fn;
}

export class ApiError extends Error {
  constructor(readonly status: number, message: string, readonly body?: unknown) {
    super(message);
    this.name = "ApiError";
  }
  /** True when re-authenticating might help. */
  get isAuth(): boolean {
    return this.status === 401 || this.status === 403;
  }
}

/**
 * The browser's own idea of today, sent as `X-Client-Date`.
 *
 * The SERVER ignores this for every decision — it only compares it against its
 * own value to warn the user that their device clock is wrong. Nothing in the
 * app reads this back as an answer.
 */
function clientDateHeader(): string {
  return new Intl.DateTimeFormat("en-CA", {
    year: "numeric", month: "2-digit", day: "2-digit",
  }).format(new Date());
}

function clientTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

async function request<T>(
  path: string,
  init: RequestInit & { retryOn401?: boolean } = {},
): Promise<T> {
  const { retryOn401 = true, ...rest } = init;

  const headers: Record<string, string> = {
    "X-Client-Date": clientDateHeader(),
    "X-Client-TZ": clientTimeZone(),
    ...(rest.body ? { "Content-Type": "application/json" } : {}),
    ...(rest.headers as Record<string, string> | undefined),
  };

  const token = getToken ? await getToken() : null;
  if (token) headers.Authorization = `Bearer ${token}`;

  const res = await fetch(path, { ...rest, headers });

  // One silent retry: the token may have expired between the provider handing
  // it over and the request landing.
  if (res.status === 401 && retryOn401 && getToken) {
    const fresh = await getToken();
    if (fresh && fresh !== token) {
      return request<T>(path, { ...init, retryOn401: false });
    }
  }

  const text = await res.text();
  let body: unknown = null;
  if (text) {
    try { body = JSON.parse(text); } catch { body = text; }
  }

  if (!res.ok) {
    const message =
      (body && typeof body === "object" && "error" in body
        ? String((body as { error: unknown }).error)
        : null) ?? `Request failed (${res.status}).`;
    throw new ApiError(res.status, message, body);
  }
  return body as T;
}

/**
 * The headers `request` sends, for the one caller that can't go through it:
 * the shopping live stream reads a response body as it arrives, which a
 * JSON request/response helper can't do.
 */
export async function authHeaders(): Promise<Record<string, string>> {
  const headers: Record<string, string> = {
    "X-Client-Date": clientDateHeader(),
    "X-Client-TZ": clientTimeZone(),
  };
  const token = getToken ? await getToken() : null;
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: "POST", body: JSON.stringify(body ?? {}) }),
  put: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: "PUT", body: JSON.stringify(body ?? {}) }),
  patch: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: "PATCH", body: JSON.stringify(body ?? {}) }),
  del: <T>(path: string) => request<T>(path, { method: "DELETE" }),

  /**
   * Fetch a protected image and hand back an object URL for <img src>.
   *
   * Circle photos sit behind a bearer token, and the browser does not attach
   * one to an <img> request — pointing src straight at the endpoint gets a 401
   * and a broken-image icon. The caller owns the returned URL and MUST
   * URL.revokeObjectURL it, or every scroll through a chat leaks a blob.
   */
  async objectUrl(path: string): Promise<string> {
    const token = getToken ? await getToken() : null;
    const res = await fetch(path, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) throw new ApiError(res.status, `Couldn't load that image (${res.status}).`);
    return URL.createObjectURL(await res.blob());
  },

  /** For PDF and .ics downloads, which aren't JSON. */
  async download(path: string, filename: string): Promise<void> {
    const token = getToken ? await getToken() : null;
    const res = await fetch(path, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) throw new ApiError(res.status, `Couldn't download that file (${res.status}).`);
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  },
};
